import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Password } from "primereact/password";
import { useContext, useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useError } from "src/core/hooks/useError";
import { BirthDateField } from "src/features/shared/components/BirthDateField";
import { EmailField } from "src/features/shared/components/EmailField";
import { GenderField } from "src/features/shared/components/GenderField";
import { NameField } from "src/features/shared/components/NameField";
import { Password2Field } from "src/features/shared/components/Password2Field";
import { PasswordField } from "src/features/shared/components/PasswordField";
import { PhoneField } from "src/features/shared/components/PhoneField";
import { SSNField } from "src/features/shared/components/SSNField";
import type { UpdateUserRequest } from "../../../../core/backend/clients/backendClient";
import { ToastContext } from "../../../../core/context/ToastContext";
import { useErrorHandler } from "../../../../core/error/useErrorHandler";
import { logService } from "../../../../core/services/logService";
import { useSettingsStore } from "../../../settings/services/useSettingsStore";
import { AddressField } from "../../../shared/components/AddressField";
import { useProfile } from "../../hooks/useProfile";
import { useAuthStore } from "../../services/useAuthStore";
import { useProfileStore } from "../../services/useProfileStore";
import type { RegisterFormData } from "../RegisterPage/RegisterPage";
import "./ProfilePage.css";

export interface ProfileFormData extends RegisterFormData {
  passwordori: string;
}

function ProfilePage() {
  const toastRef = useContext(ToastContext);

  const { t } = useTranslation();
  const navigate = useNavigate();
  const { birthDateLabel, ssnLabel, addressLabel, phoneLabel } = useProfile();
  const { getErrorMessage } = useError();

  const authStore = useAuthStore();
  const profileStore = useProfileStore();
  const settingsStore = useSettingsStore();
  const errorHandler = useErrorHandler();

  const form = useForm<ProfileFormData>({
    mode: "all",
  });

  useEffect(() => {
    form.reset({
      ...authStore.user,
    } as unknown as ProfileFormData);
  }, []);

  const onSave = async () => {
    if (!form.formState.isValid) return;

    const formValues = form.getValues();

    logService.log("ProfilePage.onSave", formValues);

    const request = { ...formValues } as UpdateUserRequest;

    await profileStore
      .updateUser(request)
      .then(async (response) => {
        // Adatok visszaírása storeba
        authStore.user.name = response.name ?? "";
        authStore.user.birthDate = response.birthDate;
        authStore.user.gender = response.gender;
        authStore.user.ssn = response.ssn;
        authStore.user.ssnConsent = response.ssnConsent;
        authStore.user.address = response.address;
        authStore.user.phone = response.phone;

        toastRef?.current?.show({
          severity: "success",
          summary: t("Home.Profile.UserDataModify"),
          detail: t("Home.Profile.UserDataModifyOK"),
          closable: true,
          life: 5000,
        });

        const oldPassword = formValues.passwordori;
        const newPassword = formValues.password;

        if (oldPassword && newPassword && oldPassword != newPassword) {
          await profileStore
            .updatePassword({
              oldPassword: oldPassword,
              newPassword: newPassword,
            })
            .then(() => {
              toastRef?.current?.show({
                severity: "success",
                summary: t("Home.Profile.PasswordChange"),
                detail: t("Home.Profile.PasswordChangeOK"),
                closable: true,
                life: 5000,
              });
            })
            .catch((error) => {
              errorHandler.setError({
                title: t("Home.Profile.PasswordChange"),
                message:
                  t("Home.Profile.PasswordChangeFailed") +
                  " " +
                  getErrorMessage(error),
              });
            });
        }

        navigate("/home");
      })
      .catch((error) => {
        errorHandler.setError({
          title: t("Home.Profile.UserDataModify"),
          message:
            t("Home.Profile.UserDataModifyFailed") +
            " " +
            getErrorMessage(error),
        });
      });
  };

  return (
    <form>
      <div className="profilePage flex justify-content-center">
        <Card
          title={t("Home.Profile.Title")}
          className="card"
          footer={
            <div className="flex gap-2">
              <div className="flex-grow-1">
                <Button
                  type="button"
                  label={t("Base.Delete")}
                  icon="pi pi-trash"
                  severity="danger"
                  disabled={!form.formState.isValid}
                  loading={profileStore.loading}
                  onClick={() => navigate("/profiledelete")}
                ></Button>
              </div>
              <div className="flex justify-content-between gap-2">
                <Button
                  type="button"
                  label={t("Home.Profile.Save")}
                  icon="pi pi-check"
                  severity="success"
                  disabled={!form.formState.isValid}
                  loading={authStore.loading}
                  onClick={onSave}
                ></Button>

                <Button
                  type="button"
                  label={t("Base.Cancel")}
                  icon="pi pi-times"
                  severity="warning"
                  onClick={() => navigate("/home")}
                ></Button>
              </div>
            </div>
          }
        >
          <div className="field grid">
            <label
              htmlFor="name"
              className="col-12 md:col-4 font-bold"
            >
              {t("Home.Registration.Name")} *:
            </label>
            <div className="col-12 md:col-8">
              <NameField
                form={form}
                required={true}
              />
            </div>
          </div>

          <div className="field grid">
            <label
              htmlFor="email"
              className="col-12 md:col-4 font-bold"
            >
              {t("Home.Login.Email")} *:
            </label>
            <div className="col-12 md:col-8">
              <EmailField
                form={form}
                disabled={true}
                required={true}
              />
            </div>
          </div>

          <div className="field grid">
            <label
              htmlFor="birthDate"
              className="col-12 md:col-4 font-bold"
            >
              {birthDateLabel} :
            </label>
            <div className="col-12 md:col-8">
              <BirthDateField
                form={form}
                required={
                  settingsStore.legacySettings.birthDateRequired ?? false
                }
              />
            </div>
          </div>

          <div className="field grid">
            <label className="col-12 md:col-4 font-bold">
              {t("Home.Registration.Gender")} *:
            </label>
            <div className="col-12 md:col-8 flex gap-2">
              <GenderField
                form={form}
                required={true}
              />
            </div>
          </div>

          <div className="field grid">
            <label
              htmlFor="ssn"
              className="col-12 md:col-4 font-bold"
            >
              {ssnLabel} :
            </label>
            <div className="col-12 md:col-8">
              <SSNField
                form={form}
                required={settingsStore.legacySettings.ssnRequired ?? false}
              />
            </div>
          </div>

          <div className="field grid">
            <label className="col-12 md:col-4 font-bold">
              {addressLabel} :
            </label>
            <div className="col-12 md:col-8">
              <AddressField
                form={form}
                required={settingsStore.legacySettings.addressRequired ?? false}
              />
            </div>
          </div>

          <div className="field grid">
            <label
              htmlFor="phone"
              className="col-12 md:col-4 font-bold"
            >
              {phoneLabel} :
            </label>
            <div className="col-12 md:col-8">
              <PhoneField
                form={form}
                required={settingsStore.legacySettings.phoneRequired ?? false}
              />
            </div>
          </div>

          <label className="col-12 block text-2xl font-bold">
            {t("Home.Profile.PasswordChange")}
          </label>

          <div className="field grid">
            <label
              htmlFor="pro_passwordori"
              className="col-12 md:col-4 font-bold white-space-nowrap"
            >
              {t("Home.ChangePassword.OriginalPassword")} *:
            </label>
            <div className="col-12 md:col-8">
              <Controller
                name="passwordori"
                control={form.control}
                rules={{
                  minLength: {
                    value: 6,
                    message: t("Error.MinLength", { value: 6 }),
                  },
                }}
                render={({ field }) => (
                  <Password
                    inputId="pro_passwordori"
                    {...field}
                    feedback={false}
                    toggleMask
                    className="w100"
                    inputClassName="w100"
                    invalid={!!form.formState.errors.passwordori}
                  />
                )}
              />
              {form.formState.errors.passwordori && (
                <small className="p-error block">
                  {form.formState.errors.passwordori.message}
                </small>
              )}
            </div>
          </div>

          <div className="field grid">
            <label
              htmlFor="password"
              className="col-12 md:col-4 font-bold"
            >
              {t("Home.Login.Password")} *:
            </label>
            <div className="col-12 md:col-8">
              <PasswordField
                form={form}
                required={false}
                feedback={true}
              />
            </div>
          </div>

          <div className="field grid">
            <label
              htmlFor="password2"
              className="col-12 md:col-4 font-bold white-space-nowrap"
            >
              {t("Home.Registration.Password2")} *:
            </label>
            <div className="col-12 md:col-8">
              <Password2Field
                form={form}
                required={false}
              />
            </div>
          </div>
        </Card>
      </div>
    </form>
  );
}

export default ProfilePage;
